'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  BodyDefault,
  BodyStrong,
  EntityIcon,
  ArchivedLifecycleBadge,
  SeverityIndicator,
  Tooltip,
  formatCategoryForDisplay,
  formatFindingStatusForDisplay,
  getFindingAssetDisplayText,
  isArchivedLifecycle,
  type EntityIconType,
  type FindingAssetItem,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import { AssetTypeSchema } from '@terra/shared-schemas-orchestrator';
import { resolveFindingsPublishedBadgeLabel } from '../../../utils/findingsPublishedFilter';
import {
  CheckmarkCircle01Icon,
  CircleIcon,
  Clock01Icon,
  HierarchyIcon,
  RefreshIcon,
  Tick01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FindingsRowActionsMenu } from '../FindingsRowActionsMenu';
import { rowClickBlockerProps } from '../rowClickBlocker';
import { ListPageEmptyCell } from '../../common/ListPageEmptyCell';
import { ListPageTablePill } from '../../common/ListPageTablePill';
import { useFindingsTableContext } from './FindingsTableContext';
import { PendingSeverityChangePill } from './PendingSeverityChangePill';

type FindingCellProps = { finding: InternalEnrichedFinding };

const FINDING_STATUS_ICON = {
  Unresolved: {
    icon: CircleIcon,
    color: token('colors.fg.status.unresolved'),
  },
  InProgress: {
    icon: Clock01Icon,
    color: token('colors.fg.status.inProgress'),
  },
  Retest: {
    icon: RefreshIcon,
    color: token('colors.fg.status.inProgress'),
  },
  Resolved: {
    icon: CheckmarkCircle01Icon,
    color: token('colors.fg.status.resolved'),
  },
  FalsePositive: {
    icon: Tick01Icon,
    color: token('colors.fg.status.duplicate'),
  },
  WontFix: {
    icon: Tick01Icon,
    color: token('colors.fg.status.duplicate'),
  },
} as const satisfies Record<
  InternalEnrichedFinding['status'],
  { icon: TerraIcon; color: string }
>;

const ASSET_ENTITY_ICON_TYPE: Record<string, EntityIconType> = {
  [AssetTypeSchema.enum.ENDPOINT]: 'endpoint',
  [AssetTypeSchema.enum.WEBPAGE]: 'webPage',
};

const toAssetEntityIconType = (asset: FindingAssetItem): EntityIconType =>
  ASSET_ENTITY_ICON_TYPE[asset.type] ?? 'asset';

function FindingsEntityLink({
  iconType,
  label,
  onClick,
  trailing,
}: {
  iconType: EntityIconType;
  label: string;
  onClick: () => void;
  trailing?: ReactNode;
}) {
  return (
    <HStack gap={2} minW={0} w="full">
      <Box flexShrink={0} display="inline-flex">
        <EntityIcon
          type={iconType}
          appearance="highlighted"
          density="compact"
        />
      </Box>
      <Box
        as="span"
        minW={0}
        cursor="pointer"
        _hover={{ textDecoration: 'underline' }}
        {...rowClickBlockerProps}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <BodyStrong color="fg.neutral.muted" truncate title={label}>
          {label}
        </BodyStrong>
      </Box>
      {trailing}
    </HStack>
  );
}

export function FindingStatusIconCell({ finding }: FindingCellProps) {
  const { icon: StatusIcon, color } = FINDING_STATUS_ICON[finding.status];
  const label = formatFindingStatusForDisplay(finding.status);

  return (
    <HStack gap={1.5} minW={0} w="full" alignItems="center" title={label}>
      <Box flexShrink={0} display="inline-flex" aria-hidden>
        <StatusIcon size={16} color={color} />
      </Box>
      <BodyDefault color="fg.default" truncate>
        {label}
      </BodyDefault>
    </HStack>
  );
}

export function FindingSeverityCell({ finding }: FindingCellProps) {
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.overview.severityChangeRequest',
  );
  const { isFeatureFlagEnabled: isSeverityChangeRequestEnabled } =
    useFeatureFlags('SEVERITY_CHANGE_REQUEST_ENABLED');
  const pendingProposedSeverity = finding.pendingProposedSeverity;
  const hasPendingRequest =
    isSeverityChangeRequestEnabled && !!pendingProposedSeverity;
  const tooltipTitle = hasPendingRequest
    ? t('gridPendingTooltip', {
        current: finding.severity,
        proposed: pendingProposedSeverity,
      })
    : finding.severity;

  return (
    <Tooltip title={tooltipTitle}>
      <HStack
        gap={1.5}
        alignItems="center"
        h={6}
        flexShrink={0}
        aria-label={tooltipTitle}
      >
        <SeverityIndicator severity={finding.severity} size="md" />
        {hasPendingRequest && pendingProposedSeverity ? (
          <PendingSeverityChangePill
            proposedSeverity={pendingProposedSeverity}
            label={t('gridPendingPill', { severity: pendingProposedSeverity })}
          />
        ) : null}
      </HStack>
    </Tooltip>
  );
}

export function FindingTitleCell({ finding }: FindingCellProps) {
  const t = useTranslations('designSystem.findingsList.table');
  const isChained = (finding.constituentFindingIds?.length ?? 0) > 0;

  return (
    <HStack gap={2} minW={0} w="full">
      {isChained ? (
        <Box flexShrink={0} display="inline-flex">
          <Tooltip title={t('chainedTooltip')}>
            <HierarchyIcon size={14} aria-label={t('chainedTooltip')} />
          </Tooltip>
        </Box>
      ) : null}
      <ArchivedLifecycleBadge lifecycle={finding.lifecycle} />
      <Tooltip
        title={finding.title}
        positioning={{ placement: 'bottom-start' }}
        whiteSpace="normal"
      >
        <Box minW={0} flex={1}>
          <BodyStrong color="fg.neutral.default" truncate>
            {finding.title}
          </BodyStrong>
        </Box>
      </Tooltip>
    </HStack>
  );
}

export function FindingCategoryCell({ finding }: FindingCellProps) {
  if (!finding.category) {
    return <ListPageEmptyCell />;
  }

  const categoryLabel = formatCategoryForDisplay(finding.category);

  return (
    <Tooltip title={categoryLabel}>
      <Box minW={0} maxW="full" w="full">
        <ListPageTablePill>{categoryLabel}</ListPageTablePill>
      </Box>
    </Tooltip>
  );
}

export function FindingRelatedAssetCell({ finding }: FindingCellProps) {
  const t = useTranslations('designSystem.findings.findingAsset');
  const { onOpenAsset } = useFindingsTableContext();
  const [asset, ...rest] = finding.assets ?? [];

  if (!asset) {
    return <ListPageEmptyCell />;
  }

  return (
    <FindingsEntityLink
      iconType={toAssetEntityIconType(asset)}
      label={getFindingAssetDisplayText(asset)}
      onClick={() => onOpenAsset(asset)}
      trailing={
        <>
          <ArchivedLifecycleBadge lifecycle={asset.lifecycle} />
          {rest.length > 0 ? (
            <Tooltip
              positioning={{ strategy: 'fixed', placement: 'top' }}
              title=""
              customContent={
                <VStack gap={1} alignItems="flex-start">
                  {rest.map((restAsset) => (
                    <BodyStrong key={restAsset.id}>
                      {getFindingAssetDisplayText(restAsset)}
                    </BodyStrong>
                  ))}
                </VStack>
              }
            >
              <Box flexShrink={0} display="inline-flex">
                <ListPageTablePill>
                  {t('moreCount', { count: rest.length })}
                </ListPageTablePill>
              </Box>
            </Tooltip>
          ) : null}
        </>
      }
    />
  );
}

export function FindingRelatedSignalCell({ finding }: FindingCellProps) {
  const { onOpenSignal } = useFindingsTableContext();
  const signal = finding.signalSummary;

  if (!signal) {
    return <ListPageEmptyCell />;
  }

  return (
    <FindingsEntityLink
      iconType="signal"
      label={signal.title ?? signal.id}
      onClick={() => onOpenSignal({ signalId: signal.id, title: signal.title })}
    />
  );
}

export function FindingRetestedCell({ finding }: FindingCellProps) {
  const t = useTranslations('designSystem.findingsList.table');
  const label = finding.wasEverRetested ? t('retestedYes') : t('retestedNo');

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {label}
    </BodyDefault>
  );
}

export function FindingPublishedCell({ finding }: FindingCellProps) {
  const t = useTranslations('designSystem.findingsList.table');
  const isPublished = finding.isPublished === true;
  const label = resolveFindingsPublishedBadgeLabel({
    isPublished,
    wasAutoPublished: finding.wasAutoPublished,
    labels: {
      unpublished: t('unpublished'),
      published: t('published'),
      autoPublished: t('autoPublished'),
    },
  });

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {label}
    </BodyDefault>
  );
}

export function FindingDateCell({
  finding,
  field,
}: FindingCellProps & { field: 'createdAt' | 'updatedAt' }) {
  const dateFormatter = useDateFormatter();

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(finding[field]))}
    </BodyDefault>
  );
}

export function FindingActionsCell({ finding }: FindingCellProps) {
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const { hasBulkSelection, rowActions } = useFindingsTableContext();
  const isArchived = isArchivedLifecycle(finding.lifecycle);

  return (
    <FindingsRowActionsMenu
      finding={finding}
      disabled={hasBulkSelection || isArchived}
      disabledTooltip={
        isArchived ? tLifecycle('archivedFindingActionTooltip') : undefined
      }
      onEdit={rowActions.onEdit}
      onDelete={rowActions.onDelete}
      onChangeStatus={rowActions.onChangeStatus}
      onTogglePublish={rowActions.onTogglePublish}
      onGenerateReport={rowActions.onGenerateReport}
    />
  );
}
